import { RefObject, useLayoutEffect, useState } from "react"
import { createPortal } from "react-dom"
import dayjs, { Dayjs } from "dayjs"
import styled from "styled-components"

import DateRangePicker from "modals/StatsReportModal/DateRangePicker"

const VIEWPORT_PADDING = 16
const GAP = 4
const PICKER_DESKTOP = { width: 580, height: 375 }
const PICKER_MOBILE = { width: 290, height: 395 }

const getPickerSize = () => (typeof window !== "undefined" && window.innerWidth <= 768 ? PICKER_MOBILE : PICKER_DESKTOP)

const Wrapper = styled.div<{ $left: number; $top: number }>`
  border-radius: 10px;
  position: fixed;
  z-index: 1001;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  border: 1px solid var(--border-subtle);
  background-color: var(--surface-1);
  box-shadow: 0px 16px 40px -8px #47556921;
`

interface CustomDatePickerPopoverProps {
  startDate?: Date | null
  endDate?: Date | null
  maxRange?: number
  maxRangeInYears?: number
  onCancel: () => void
  onApply: (dates: [Date | null, Date | null]) => void
  wrapperRef: RefObject<HTMLDivElement | null>
  anchorRef: RefObject<HTMLDivElement | null>
}

export const CustomDatePickerPopover = ({
  startDate = null,
  endDate = null,
  maxRange,
  maxRangeInYears,
  onCancel,
  onApply,
  wrapperRef,
  anchorRef,
}: CustomDatePickerPopoverProps) => {
  const selectedRange: [Dayjs | null, Dayjs | null] | undefined =
    startDate || endDate ? [startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null] : undefined

  const [position, setPosition] = useState({ left: 0, top: 0 })

  useLayoutEffect(() => {
    const anchor = anchorRef?.current
    if (!anchor) return

    const updatePosition = () => {
      if (!anchor.isConnected) return
      const { width, height } = getPickerSize()
      const rect = anchor.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight

      const anchorCenterX = rect.left + rect.width / 2
      let left = anchorCenterX - width / 2
      let top = rect.bottom + GAP

      if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING
      if (left + width > vw - VIEWPORT_PADDING) left = vw - width - VIEWPORT_PADDING
      if (top + height > vh - VIEWPORT_PADDING) top = rect.top - height - GAP
      if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING

      setPosition({ left, top })
    }

    updatePosition()
    const rafId = requestAnimationFrame(updatePosition)

    const handleScroll = () => requestAnimationFrame(updatePosition)

    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", handleScroll, true)
    document.addEventListener("scroll", handleScroll, true)
    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", handleScroll, true)
      document.removeEventListener("scroll", handleScroll, true)
    }
  }, [anchorRef])

  const handleSubmit = (dateRange: [Dayjs | null, Dayjs | null]) => {
    const [start, end] = dateRange
    const dates: [Date | null, Date | null] = [start ? start.toDate() : null, end ? end.toDate() : null]
    onApply(dates)
  }

  const content = (
    <Wrapper ref={wrapperRef} $left={position.left} $top={position.top}>
      <DateRangePicker
        selectedRange={selectedRange}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        allowNulls
        maxRange={maxRange}
        maxRangeInYears={maxRangeInYears}
      />
    </Wrapper>
  )

  return typeof document !== "undefined" ? createPortal(content, document.body) : content
}
